import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { isUndefined } from "lodash";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { Button, Dialog, DialogTitle, DialogContent, DialogActions } from "@material-ui/core";
import { RiderContext } from "../../../context";
import { FilledTextField } from "../../../components/FormElements";
import { KeyValueDisplay } from "../../../components/Typography";
import { StringUtils } from "../../../util";
import { withSnackbar } from "../../../HOC/withSnackbar";
import { errorMessageBuilder } from "../../../util/helpers";
import { useTestIds } from "../../../hooks/useTestIds";

const { formatNumber } = StringUtils;

const CashOutModal = ({ setSnackbarMessage, setAlertSeverity }) => {
  const { t } = useTranslation();
  const {
    cashOutTransaction,
    cashOutTripTransaction,
    cashOutFineTransaction,
    fetchWalletTransactions,
    walletTransactions,
  } = useContext(RiderContext);
  const testIds = useTestIds("rider-profile-cash-out-modal");

  const [isOpen, setIsOpen] = useState(true);
  const [amount, setAmount] = useState("");
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [amountError, setAmountError] = useState(false);
  const [helperText, setHelperText] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const { riderId, transactionId } = useParams();
  const history = useHistory();

  useEffect(() => {
    const num = Number(amount);
    if (amount && Number.isNaN(num)) {
      setHelperText("Not a valid number");
      setAmountError(true);
    } else if (num && num <= 0) {
      setHelperText("Must be > 0.00");
      setAmountError(true);
    } else if (num && num > currentTransaction?.cash_out_remaining) {
      setHelperText("Must be < available for cash out");
      setAmountError(true);
    } else {
      setHelperText(null);
      setAmountError(false);
    }
  }, [amount]);

  useEffect(() => {
    if (!walletTransactions) fetchWalletTransactions(transactionId);
  }, [transactionId]);

  useEffect(() => {
    if (walletTransactions)
      setCurrentTransaction(walletTransactions.find((tr) => tr.id === transactionId));
  }, [walletTransactions]);

  const handleClose = (e = null) => {
    if (e) e.stopPropagation();
    setIsOpen(false);
    setIsLoading(false);
    history.goBack();
  };

  const getErrorMessage = (error) => {
    let errorMessage = "";

    if (!isUndefined(error.response) && !isUndefined(error.response.data?.non_field_errors)) {
      const APIErrorResponse = error.response.data?.non_field_errors[0];
      errorMessage = errorMessageBuilder(
        t("Riders.Wallet.RequestError"),
        error.response.status,
        APIErrorResponse,
      );
    } else {
      errorMessage = error.message;
    }

    return errorMessage;
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    if (amountError) {
      setIsLoading(false);
      return;
    }

    try {
      // We can cash out either "Wallet - Deposit" (BANK_TRANSFER) or Trip related (TRIP_PAYMENT, CARD_HOLD) transactions
      if (currentTransaction.reference_type === "BANK_TRANSFER") {
        await cashOutTransaction(Number(amount), transactionId, riderId);
      } else if (currentTransaction.reference_type === "FINE_CHARGE") {
        await cashOutFineTransaction(Number(amount), transactionId, currentTransaction.trip_id);
      } else {
        await cashOutTripTransaction(Number(amount), transactionId, currentTransaction.trip_id);
      }
      setSnackbarMessage(t("Riders.Wallet.RequestSuccessful"));
      setAlertSeverity("success");
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setSnackbarMessage(errorMessage);
      setAlertSeverity("error");
    }
    handleClose();
  };

  return (
    <Dialog
      data-testid={testIds.wrapper}
      open={isOpen}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle data-testid={testIds.title}>
        {t("Riders.WalletTransactions.CashOut")}
      </DialogTitle>
      <DialogContent data-testid={testIds.content}>
        <KeyValueDisplay
          label={t("Riders.WalletTransactions.AvailableForCashOut")}
          text={formatNumber(currentTransaction?.cash_out_remaining, currentTransaction?.currency)}
          justify="space-between"
          testIds={{
            label: testIds.availableLabel,
            text: testIds.availableValue,
          }}
        />
        <FilledTextField
          value={amount}
          mask={currentTransaction?.currency}
          onChange={(e) => setAmount(e.target.value)}
          fullWidth
          label={t("Riders.Wallet.Amount")}
          error={amountError}
          helperText={helperText}
          testIds={{
            input: testIds.amountInput,
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button data-testid={testIds.cancelButton} onClick={handleClose}>
          {t("Riders.Wallet.Cancel")}
        </Button>
        <Button data-testid={testIds.saveButton} disabled={isLoading} onClick={handleSubmit}>
          {t("Riders.Wallet.Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default withSnackbar(CashOutModal);
CashOutModal.propTypes = {
  setSnackbarMessage: PropTypes.func.isRequired,
  setAlertSeverity: PropTypes.func.isRequired,
};
